/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Locale, LocaleKeys } from '../../../core/locale';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setMyShowsUserSettingsErrorAction } from '../../../store/settings/userpreferences/myshowsoptionsmenu/myshowsoptionsmenu.actions';
import { setOnePassAndRecordingErrorAction } from '../../../store/settings/userpreferences/onepassandrecordings/onepassandrecordings.actions';
import { setUserPreferencesOptionsList } from '../../../store/settings/userpreferences/userpreferences.reducers';
import { showWhisper } from '../../../store/whisper/whisper.reducer';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const UserPreferences: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setisCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector(
        (state: RootState) => state.userPreferences,
    );
    const { hasError: onePassAndRecordingError } = useTivoSelector(
        (state: RootState) => state.onePassAndRecordingOptions,
    );
    const { hasError: myShowsOptionsError } = useTivoSelector(
        (state: RootState) => state.myShowsUserSettings,
    );
    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.USER_PREFERENCES, true));
    };

    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.title === selectedOption?.title ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    useEffect(() => {
        dispatch(setUserPreferencesOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        dispatch(setUserPreferencesOptionsList());

        if (onePassAndRecordingError || myShowsOptionsError) {
            dispatch(
                showWhisper({
                    message: Locale.getString(LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION),
                    iconType: WhisperTypes.INFO,
                }),
            );
            dispatch(setOnePassAndRecordingErrorAction(false));
            dispatch(setMyShowsUserSettingsErrorAction(false));
        }
    }, [onePassAndRecordingError, myShowsOptionsError, isNetworkDisconnected]);

    const renderUserPrefList = () => {
        return (
            <div className="settings-common-content multi-value-item">
                <List
                    type={ListTypes.USER_PREFERENCES}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.USER_PREFERENCES}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.title}</span>
                            {item.value && <span className="list-value">[{item.value}]</span>}
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_USER_PREFERENCES_TITLE)}>
            {renderUserPrefList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default UserPreferences;
